/**
 * Voice Memo Client Helpers
 *
 * Shared browser helpers for recording, uploading, and transcribing voice memos.
 *
 * Primary responsibilities:
 * - Choose a MediaRecorder audio type supported by the browser
 * - Upload recorded audio through the voice-notes API
 * - Build chat-compatible voice memo metadata after transcription
 *
 * Dependencies:
 * - /api/voice-notes/upload
 * - /api/speech-to-text
 */

import {
  buildChatVoiceMemoMetadata,
  hasMeaningfulVoiceMemoWaveform,
  normalizeVoiceMemoWaveformSamples
} from '@/lib/chat/voice-memo'
import { getVoiceNotePersonalDictionaryHints } from '@/lib/chat/voice-note-dictionary'
import type { ChatVoiceMemoMetadata } from '@/types/voice-memo'

const VOICE_INPUT_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg'
] as const

type VoiceMemoUploadResult = {
  url: string
  path: string | null
  contentType: string
  sizeBytes: number
}

type AudioContextConstructor = new () => AudioContext

const VOICE_MEMO_WAVEFORM_SAMPLE_COUNT = 48
const MIN_DECODED_SPEECH_RMS = 0.006
const MIN_DECODED_SPEECH_PEAK = 0.035
const MIN_DECODED_ACTIVE_BUCKET_RMS = 0.004
const MIN_DECODED_ACTIVE_BUCKET_RATIO = 0.08

type DecodedAudioAnalysis = {
  hasSpeechLikeAudio: boolean
  waveformSamples: number[]
}

// --------------

export function getVoiceInputMimeType() {
  if (typeof MediaRecorder === 'undefined') {
    return undefined
  }

  return VOICE_INPUT_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type))
}

// --------------

function getVoiceInputExtension(mimeType: string) {
  if (mimeType.includes('mp4')) return 'm4a'
  if (mimeType.includes('mpeg')) return 'mp3'
  return 'webm'
}

// --------------

async function readAudioDurationSeconds(audioBlob: Blob) {
  if (typeof Audio === 'undefined' || typeof URL === 'undefined') {
    return null
  }

  return new Promise<number | null>((resolve) => {
    const audioUrl = URL.createObjectURL(audioBlob)
    const audio = new Audio()
    const cleanup = () => {
      URL.revokeObjectURL(audioUrl)
    }
    const timeout = window.setTimeout(() => {
      cleanup()
      resolve(null)
    }, 2000)

    audio.preload = 'metadata'
    audio.onloadedmetadata = () => {
      window.clearTimeout(timeout)
      const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null
      cleanup()
      resolve(duration)
    }
    audio.onerror = () => {
      window.clearTimeout(timeout)
      cleanup()
      resolve(null)
    }
    audio.src = audioUrl
  })
}

// --------------

function getBrowserAudioContext() {
  if (typeof window === 'undefined') {
    return null
  }

  return (
    window.AudioContext ??
    (
      window as Window & {
        webkitAudioContext?: AudioContextConstructor
      }
    ).webkitAudioContext ??
    null
  )
}

// --------------

function readDecodedAudioAnalysis(buffer: AudioBuffer): DecodedAudioAnalysis {
  if (buffer.length === 0) {
    return {
      hasSpeechLikeAudio: false,
      waveformSamples: []
    }
  }

  const channel = buffer.getChannelData(0)
  const bucketSize = Math.max(1, Math.floor(channel.length / VOICE_MEMO_WAVEFORM_SAMPLE_COUNT))
  let activeBucketCount = 0
  let totalSum = 0
  let totalPeak = 0
  const samples: number[] = []

  for (let index = 0; index < VOICE_MEMO_WAVEFORM_SAMPLE_COUNT; index += 1) {
    const start = index * bucketSize
    const end = Math.min(channel.length, start + bucketSize)
    let sum = 0
    let peak = 0

    for (let cursor = start; cursor < end; cursor += 1) {
      const level = Math.abs(channel[cursor] ?? 0)
      sum += level * level
      totalSum += level * level
      peak = Math.max(peak, level)
      totalPeak = Math.max(totalPeak, level)
    }

    const length = Math.max(1, end - start)
    const rms = Math.sqrt(sum / length)
    if (rms >= MIN_DECODED_ACTIVE_BUCKET_RMS || peak >= MIN_DECODED_SPEECH_PEAK) {
      activeBucketCount += 1
    }

    samples.push(Math.min(1, Math.max(rms * 8, peak * 1.8)))
  }

  const overallRms = Math.sqrt(totalSum / channel.length)
  const activeBucketRatio = activeBucketCount / VOICE_MEMO_WAVEFORM_SAMPLE_COUNT

  return {
    hasSpeechLikeAudio:
      overallRms >= MIN_DECODED_SPEECH_RMS &&
      totalPeak >= MIN_DECODED_SPEECH_PEAK &&
      activeBucketRatio >= MIN_DECODED_ACTIVE_BUCKET_RATIO,
    waveformSamples: normalizeVoiceMemoWaveformSamples(samples)
  }
}

// --------------

async function readAudioAnalysis(audioBlob: Blob) {
  const AudioContextCtor = getBrowserAudioContext()
  if (!AudioContextCtor) {
    return null
  }

  const audioContext = new AudioContextCtor()

  try {
    const buffer = await audioContext.decodeAudioData(await audioBlob.arrayBuffer())
    return readDecodedAudioAnalysis(buffer)
  } catch {
    return null
  } finally {
    if (audioContext.state !== 'closed') {
      void audioContext.close().catch(() => undefined)
    }
  }
}

// --------------

async function uploadVoiceMemoAudio(
  audioFile: File,
  isAuthenticated: boolean
): Promise<VoiceMemoUploadResult> {
  if (!isAuthenticated) {
    return {
      url: URL.createObjectURL(audioFile),
      path: null,
      contentType: audioFile.type || 'audio/webm',
      sizeBytes: audioFile.size
    }
  }

  const formData = new FormData()
  formData.append('audio', audioFile)

  const response = await fetch('/api/voice-notes/upload', {
    method: 'POST',
    body: formData
  })
  const data = (await response.json().catch(() => ({}))) as {
    contentType?: unknown
    error?: unknown
    path?: unknown
    sizeBytes?: unknown
    url?: unknown
  }

  if (!response.ok) {
    throw new Error(typeof data.error === 'string' ? data.error : 'Voice memo upload failed')
  }

  if (typeof data.url !== 'string' || !data.url.trim()) {
    throw new Error('Voice memo upload returned no audio URL')
  }

  return {
    url: data.url.trim(),
    path: typeof data.path === 'string' && data.path.trim() ? data.path.trim() : null,
    contentType:
      typeof data.contentType === 'string' && data.contentType.trim()
        ? data.contentType.trim()
        : audioFile.type || 'audio/webm',
    sizeBytes: typeof data.sizeBytes === 'number' ? data.sizeBytes : audioFile.size
  }
}

// --------------

export async function transcribeRecordedVoiceMemo(input: {
  /** Live (LiveKit) transcript captured while recording. Skips batch STT when set. */
  liveTranscript?: string | null
  audioBlob: Blob
  fallbackDurationSeconds?: number | null
  isAuthenticated: boolean
  waveformSamples?: number[]
}): Promise<ChatVoiceMemoMetadata> {
  if (input.audioBlob.size === 0) {
    throw new Error('No voice input was captured')
  }

  const fallbackWaveformSamples = normalizeVoiceMemoWaveformSamples(input.waveformSamples)
  const decodedAnalysis = await readAudioAnalysis(input.audioBlob)

  if (decodedAnalysis && !decodedAnalysis.hasSpeechLikeAudio) {
    throw new Error('No speech was detected')
  }

  if (
    !decodedAnalysis &&
    fallbackWaveformSamples.length > 0 &&
    !hasMeaningfulVoiceMemoWaveform(fallbackWaveformSamples)
  ) {
    throw new Error('No speech was detected')
  }

  const decodedWaveformSamples = decodedAnalysis?.waveformSamples ?? null
  const waveformSamples = decodedWaveformSamples ?? fallbackWaveformSamples

  if (waveformSamples.length > 0 && !hasMeaningfulVoiceMemoWaveform(waveformSamples)) {
    throw new Error('No speech was detected')
  }

  const mimeType = input.audioBlob.type || 'audio/webm'
  const extension = getVoiceInputExtension(mimeType)
  const audioFile = new File([input.audioBlob], `voice-input.${extension}`, {
    type: mimeType
  })
  const formData = new FormData()
  formData.append('audio', audioFile)

  const personalDictionary = getVoiceNotePersonalDictionaryHints()
  if (personalDictionary) {
    formData.append('personalDictionary', personalDictionary)
  }

  const liveTranscript =
    typeof input.liveTranscript === 'string' ? input.liveTranscript.trim() : ''

  // Live dictation already produced the transcript: keep the audio upload
  // (memo playback) but skip the batch speech-to-text round trip.
  const [uploadResult, measuredDurationSeconds, response] = await Promise.all([
    uploadVoiceMemoAudio(audioFile, input.isAuthenticated),
    readAudioDurationSeconds(input.audioBlob),
    liveTranscript
      ? Promise.resolve(null)
      : fetch('/api/speech-to-text', {
          method: 'POST',
          body: formData
        })
  ])
  const data = (response
    ? await response.json().catch(() => ({}))
    : {}) as {
    error?: unknown
    text?: unknown
    transcript?: unknown
  }

  if (response && !response.ok) {
    throw new Error(typeof data.error === 'string' ? data.error : 'Voice transcription failed')
  }

  const transcript =
    liveTranscript ||
    (typeof data.text === 'string'
      ? data.text.trim()
      : typeof data.transcript === 'string'
        ? data.transcript.trim()
        : '')

  if (!transcript) {
    throw new Error('No speech was detected')
  }

  return buildChatVoiceMemoMetadata({
    audioUrl: uploadResult.url,
    audioPath: uploadResult.path,
    contentType: uploadResult.contentType,
    sizeBytes: uploadResult.sizeBytes,
    durationSeconds: measuredDurationSeconds ?? input.fallbackDurationSeconds ?? null,
    transcript,
    transcriptionStatus: 'transcribed',
    waveformSamples
  })
}
